/**
 * Pure dice/card rules. No I/O, no randomness — imported by the server (authoritative)
 * and by the web client (UI hints only).
 *
 * Vocabulary, taken from the card data in the database:
 * - A card's `killer` code is the dice combination that destroys that card when attacked.
 * - A card's `power` code is what its holder may do to their own dice.
 */
import { DICE_COUNT, DIE_FACES, PLAN_LOCK_MS, PLAN_SETTLE_MS, PLAN_STEP_MS } from './constants.js';
import { t } from './text.js';
// ---------------------------------------------------------------------------
// Dice predicates
// ---------------------------------------------------------------------------
function faceCounts(dice) {
    const counts = new Array(DIE_FACES + 1).fill(0);
    for (const d of dice)
        counts[d] = (counts[d] ?? 0) + 1;
    return counts;
}
const sum = (dice) => dice.reduce((a, b) => a + b, 0);
export const hasOfAKind = (dice, n) => faceCounts(dice).some((c) => c >= n);
export const hasPairOf = (dice, face) => faceCounts(dice)[face] >= 2;
export const hasTwoPair = (dice) => faceCounts(dice).filter((c) => c >= 2).length >= 2;
export function hasFullHouse(dice) {
    const counts = faceCounts(dice);
    const trips = counts.findIndex((c) => c >= 3);
    return trips > 0 && counts.some((c, face) => face !== trips && c >= 2);
}
/** True if the distinct faces contain a run of `length` consecutive values. */
export function hasStraight(dice, length) {
    const faces = [...new Set(dice)].sort((a, b) => a - b);
    let run = 1;
    for (let i = 1; i < faces.length; i++) {
        run = faces[i] === faces[i - 1] + 1 ? run + 1 : 1;
        if (run >= length)
            return true;
    }
    return length <= 1 && faces.length > 0;
}
/** Kadice: 1-2-3-5-6 or 1-2-4-5-6. */
export function isKadice(dice) {
    const key = [...dice].sort((a, b) => a - b).join('');
    return key === '12356' || key === '12456';
}
// ---------------------------------------------------------------------------
// Killing
// ---------------------------------------------------------------------------
/** Killer codes that only make sense with a full set of dice (e.g. "sum ≤ 10" is trivial with 2 dice). */
const FULL_HAND_KILLERS = new Set(['kadice', 'xxxyy', 'seq5', 'e', 'o', 'leq10', 'geq25']);
const KILLERS = {
    p1: (d) => hasPairOf(d, 1),
    p2: (d) => hasPairOf(d, 2),
    p3: (d) => hasPairOf(d, 3),
    p4: (d) => hasPairOf(d, 4),
    p5: (d) => hasPairOf(d, 5),
    p6: (d) => hasPairOf(d, 6),
    xxyy: hasTwoPair,
    xxx: (d) => hasOfAKind(d, 3),
    xxxx: (d) => hasOfAKind(d, 4),
    xxxyy: hasFullHouse,
    seq4: (d) => hasStraight(d, 4),
    seq5: (d) => hasStraight(d, 5),
    e: (d) => d.every((v) => v % 2 === 0),
    o: (d) => d.every((v) => v % 2 === 1),
    leq10: (d) => sum(d) <= 10,
    geq25: (d) => sum(d) >= 25,
    kadice: isKadice,
};
/** Every killer code the rules know. */
export const KILLER_CODES = Object.keys(KILLERS);
/** Can these (kept) dice destroy a card with the given killer code? */
export function canKill(dice, killer) {
    if (dice.length < DICE_COUNT && FULL_HAND_KILLERS.has(killer))
        return false;
    return KILLERS[killer]?.(dice) ?? false;
}
// ---------------------------------------------------------------------------
// Free kills
// ---------------------------------------------------------------------------
/**
 * Powers that, when the kept dice show the card's own combo, destroy extra targets
 * regardless of those targets' killers — on top of any cards the dice kill naturally.
 */
const FREE_KILLS = { xxxx: 2, seq5: 2, e: 1, o: 1, kadice: Infinity };
/** Does this power change dice or grant free kills? (Others don't affect the odds of a kill.) */
export function affectsKills(power) {
    return powerRule(power).kind !== 'none' || FREE_KILLS[power ?? ''] !== undefined;
}
/** How many targets these kept dice may destroy for free under this power (0 if not triggered). */
export function freeKills(power, dice) {
    const n = FREE_KILLS[power ?? ''];
    return n !== undefined && canKill(dice, power) ? n : 0;
}
export function powerRule(power) {
    const wild = /^w([1-6])$/.exec(power ?? '');
    if (wild)
        return { kind: 'wild', face: Number(wild[1]) };
    switch (power) {
        case 'xxx':
            return { kind: 'nudge', up: true, down: true };
        case 'leq10':
            return { kind: 'nudge', up: false, down: true };
        case 'geq25':
            return { kind: 'nudge', up: true, down: false };
        default:
            return { kind: 'none' };
    }
}
/** All five dice show the same face. */
export const isFiveOfAKind = (dice) => dice.length === DICE_COUNT && hasOfAKind(dice, DICE_COUNT);
/**
 * Five of a kind lets you set every die to anything, whatever your card's power. `wild`: the
 * turn's dice already went wild (`TurnView.wild`), or debug mode's 5x button, for testing.
 */
export function diceRule(pool, power, wild = false) {
    return wild || isFiveOfAKind(pool) ? { kind: 'any' } : powerRule(power);
}
/** Can a single die showing `original` be changed to `value` under this power? */
export function canModifyDie(rule, original, value) {
    if (value < 1 || value > DIE_FACES || value === original)
        return false;
    if (rule.kind === 'any')
        return true;
    if (rule.kind === 'wild')
        return original === rule.face;
    if (rule.kind === 'nudge') {
        return (rule.up && value === original + 1) || (rule.down && value === original - 1);
    }
    return false;
}
/**
 * Returns an error message if the modifications are not allowed, otherwise null.
 * `held`: the first `held` dice in the pool were kept from earlier turns. Those are locked —
 * they can only be discarded — unless the pool is five of a kind (rule `any`).
 */
export function validateMods(pool, mods, power, wild = false, held = 0) {
    if (mods.length === 0)
        return null;
    const rule = diceRule(pool, power, wild);
    if (rule.kind === 'none')
        return t('error.noDicePower');
    if (rule.kind === 'nudge' && mods.length > 1)
        return t('error.oneDieOnly');
    if (new Set(mods.map((m) => m.index)).size !== mods.length)
        return t('error.dieChangedTwice');
    for (const m of mods) {
        const original = pool[m.index];
        if (original === undefined)
            return t('error.invalidDie');
        if (m.index < held && rule.kind !== 'any')
            return t('error.heldDieLocked');
        if (!canModifyDie(rule, original, m.value))
            return t('error.changeNotAllowed');
    }
    return null;
}
export function applyMods(pool, mods) {
    const out = [...pool];
    for (const m of mods)
        out[m.index] = m.value;
    return out;
}
/**
 * Steps a bot's dice change takes on screen: the changed dice move one at a time, each taking one
 * step, or with the dice wild one per face on its way (counting up or down, never round from 6 to
 * 1). 0: no change.
 */
export function planChangeSteps(pool, mods, wild) {
    let steps = 0;
    for (const m of mods) {
        const from = pool[m.index] ?? m.value;
        steps += wild && from !== m.value ? Math.abs(m.value - from) : 1;
    }
    return steps;
}
/**
 * How long a bot's announced dice change plays out before it makes the move: the steps
 * (`PLAN_STEP_MS` each, the first at once), `PLAN_LOCK_MS` until the dice lock, then
 * `PLAN_SETTLE_MS` to take it in. 0: no change.
 */
export function planChangeMs(pool, mods, wild) {
    const steps = planChangeSteps(pool, mods, wild);
    return steps === 0 ? 0 : (steps - 1) * PLAN_STEP_MS + PLAN_LOCK_MS + PLAN_SETTLE_MS;
}
//# sourceMappingURL=rules.js.map